import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, Truck, PackageCheck, Send, Clock, FileText, Search } from 'lucide-react';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/auth';
import { Pill } from '../../components/TwDataTable';
import { Select, Input } from '../../ui';
import { formatIst } from '../../lib/time';
import { warehouseLabel } from '../../lib/warehouseLabel';

const qf = (n: any) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const dt = (v: any) => (v ? formatIst(v) : null);
const dateOnly = (v: any) => (v ? String(v).substring(0, 10) : null);

// Shipment status → the label + pill colour shown to a warehouse user (packed = "ready to leave").
const STATUS_META: Record<string, { label: string; color: string }> = {
  PACKED: { label: 'Packed', color: 'gold' },
  DISPATCHED: { label: 'In transit', color: 'blue' },
  DELIVERED: { label: 'Delivered', color: 'green' },
  CANCELLED: { label: 'Cancelled', color: 'red' },
};

/**
 * Dispatch register — outbound shipments from the warehouse(s). Shipment-level (a partial dispatch is its own
 * row); each row shows its current status, and expands to the full lifecycle (packed → dispatched → delivered),
 * logistics, the itemised lines with batches, and the linked invoice / challan. A Warehouse Manager sees only
 * their warehouse(s); Admin / Super Admin see all warehouses and can narrow with the warehouse filter.
 */
export function DispatchesPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isWHM = role === 'WAREHOUSE_MANAGER';

  const [status, setStatus] = useState('ALL');
  const [warehouseId, setWarehouseId] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status !== 'ALL') p.status = status;
    if (warehouseId !== 'ALL') p.warehouseId = warehouseId;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [status, warehouseId, search]);

  const { data, isLoading } = useQuery({
    queryKey: ['dispatches', params],
    queryFn: () => request<any>({ url: '/dispatches', params }),
  });
  const rows: any[] = data?.rows ?? [];
  const s = data?.summary ?? { readyToDispatch: 0, inTransit: 0, delivered: 0, cancelled: 0, dispatchedToday: 0 };

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-all'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: !isWHM,
  });

  return (
    <div>
      <h1 className="mb-3 text-xl font-semibold text-content">Dispatches</h1>

      {/* KPIs — the warehouse's outbound pipeline (independent of the filters). */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi title="Ready to dispatch" value={s.readyToDispatch} accent="border-l-warning-500" valueCls="text-warning-600" icon={<PackageCheck size={15} />} />
        <Kpi title="In transit" value={s.inTransit} accent="border-l-sky-500" valueCls="text-sky-600" icon={<Truck size={15} />} />
        <Kpi title="Dispatched today" value={s.dispatchedToday} accent="border-l-brand-500" valueCls="text-brand-600" icon={<Send size={15} />} />
        <Kpi title="Delivered" value={s.delivered} accent="border-l-success-500" valueCls="text-success-600" icon={<Clock size={15} />} />
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5">
        <Select
          value={status} onChange={setStatus} size="sm" className="w-44"
          options={[
            { value: 'ALL', label: 'All statuses' },
            { value: 'PACKED', label: 'Packed (ready)' },
            { value: 'DISPATCHED', label: 'In transit' },
            { value: 'DELIVERED', label: 'Delivered' },
            { value: 'CANCELLED', label: 'Cancelled' },
          ]}
        />
        {!isWHM && (
          <Select
            value={warehouseId} onChange={setWarehouseId} size="sm" className="w-56"
            options={[{ value: 'ALL', label: 'All warehouses' }, ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: warehouseLabel(w) }))]}
          />
        )}
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Shipment / order no…" size="sm" className="w-56 pl-8" />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-muted">
              <th className="w-8 px-2 py-2.5"></th>
              <th className="px-3 py-2.5">Shipment</th>
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Order · Retailer</th>
              {!isWHM && <th className="px-3 py-2.5">Warehouse</th>}
              <th className="px-3 py-2.5 text-right">Qty</th>
              <th className="px-3 py-2.5">Carrier / vehicle</th>
              <th className="px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={isWHM ? 7 : 8} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={isWHM ? 7 : 8} className="px-3 py-10 text-center text-muted">No dispatches match these filters.</td></tr>}
            {rows.map((r) => {
              const isOpen = open.has(String(r.id));
              const meta = STATUS_META[r.status] ?? { label: r.status, color: 'default' };
              return (
                <ExpandableRow key={r.id} r={r} isOpen={isOpen} onToggle={() => toggle(String(r.id))} meta={meta} isWHM={isWHM} />
              );
            })}
          </tbody>
        </table>
      </div>
      {data?.truncated && (
        <div className="mt-2 text-xs text-subtle">Showing the 500 most recent shipments — the KPIs above still cover the whole warehouse. Narrow with the filters to find older ones.</div>
      )}
    </div>
  );
}

function ExpandableRow({ r, isOpen, onToggle, meta, isWHM }: { r: any; isOpen: boolean; onToggle: () => void; meta: { label: string; color: string }; isWHM: boolean }) {
  const cols = isWHM ? 7 : 8;
  return (
    <>
      <tr className="cursor-pointer border-b border-line/70 hover:bg-surface-2" onClick={onToggle}>
        <td className="px-2 py-2.5 text-muted">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
        <td className="px-3 py-2.5 font-medium text-brand-700 dark:text-brand-400">{r.shipmentNo}</td>
        <td className="whitespace-nowrap px-3 py-2.5 text-muted">{dateOnly(r.dispatchedAt) ?? dateOnly(r.packedAt) ?? '—'}</td>
        <td className="px-3 py-2.5">
          <div className="font-medium text-content">{r.orderNo ?? `#${r.orderId}`}</div>
          <div className="text-xs text-subtle">{r.retailerCode ? `${r.retailerCode} · ` : ''}{r.retailerName ?? `#${r.retailerId}`}{r.retailerCity ? ` · ${r.retailerCity}` : ''}</div>
        </td>
        {!isWHM && <td className="px-3 py-2.5 text-muted">{r.warehouseCode ?? r.warehouseName ?? '—'}</td>}
        <td className="whitespace-nowrap px-3 py-2.5 text-right">
          <div className="font-medium tabular-nums text-content">{qf(r.totalQty)}</div>
          <div className="text-xs text-subtle">{r.itemCount} item{r.itemCount === 1 ? '' : 's'}</div>
        </td>
        <td className="px-3 py-2.5 text-muted">{r.carrier || r.vehicleNo ? <span>{r.carrier ?? ''}{r.carrier && r.vehicleNo ? ' · ' : ''}{r.vehicleNo ?? ''}</span> : <span className="text-subtle">—</span>}</td>
        <td className="px-3 py-2.5"><Pill color={meta.color}>{meta.label}</Pill></td>
      </tr>
      {isOpen && (
        <tr className="border-b border-line bg-surface-2/40">
          <td colSpan={cols} className="p-0">
            <div className="px-5 py-4">
              <div className="grid gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.15fr)_186px]">
                {/* Shipment details */}
                <div>
                  <ColHead>Shipment details</ColHead>
                  <DetailGrid rows={[
                    [{ label: 'Warehouse', value: r.warehouseCode ?? r.warehouseName }, { label: 'Total qty', value: `${qf(r.totalQty)} · ${r.itemCount} item${r.itemCount === 1 ? '' : 's'}` }],
                    [{ label: 'Carrier', value: r.carrier }, { label: 'Vehicle', value: r.vehicleNo }],
                    [{ label: 'LR / docket', value: r.lrNo }, { label: 'Driver', value: r.driverName ? `${r.driverName}${r.driverMobile ? ` · ${r.driverMobile}` : ''}` : null }],
                    [{ label: 'Expected', value: dt(r.expectedAt) }, { label: 'POD', value: r.podUrl ? <a href={r.podUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-medium text-brand-700 hover:underline dark:text-brand-400">View proof →</a> : null }],
                  ]} />
                </div>

                {/* Items */}
                <div>
                  <ColHead>Items · {r.itemCount}</ColHead>
                  <div className="overflow-hidden rounded-control border border-line bg-surface">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-subtle">
                          <th className="px-3 py-1.5 font-medium">Product</th>
                          <th className="px-3 py-1.5 font-medium">Batch · Exp</th>
                          <th className="px-3 py-1.5 text-right font-medium">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(r.items ?? []).map((it: any, i: number) => (
                          <tr key={i} className="border-b border-line/50 last:border-0">
                            <td className="px-3 py-1.5 font-medium text-content">{it.productName || '—'}</td>
                            <td className="px-3 py-1.5 text-muted">{it.batchNo ?? '—'}{it.expiry ? <span className="text-subtle"> · {dateOnly(it.expiry)}</span> : ''}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-content">
                              {qf(it.qty)}
                              {Number(it.unitsPerBox) > 0 && it.boxQuantity != null && (
                                <div className="text-2xs font-normal text-subtle">{qf(it.boxQuantity)} box + {qf(it.looseUnitQuantity)} loose · {it.unitsPerBox}/box</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {(r.items?.length ?? 0) > 1 && (
                        <tfoot>
                          <tr className="border-t border-line">
                            <td colSpan={2} className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-subtle">Total</td>
                            <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-content">{qf(r.totalQty)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Status timeline */}
                <div>
                  <ColHead>Status timeline</ColHead>
                  <VerticalTimeline r={r} />
                </div>
              </div>

              {/* Linked documents */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {r.invoiceNo && (
                  <DocChip icon={<FileText size={13} />} label="Invoice" value={r.invoiceNo} to={r.invoiceId ? `/billing/invoices/${r.invoiceId}` : undefined} warn={r.verificationStatus && r.verificationStatus !== 'VERIFIED' ? String(r.verificationStatus).toLowerCase() : undefined} />
                )}
                {r.challanNo && <DocChip icon={<FileText size={13} />} label="Challan" value={r.challanNo} />}
                <Link to={`/orders/${r.orderId}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-control border border-line bg-surface px-3.5 py-1.5 text-sm font-medium text-content transition-colors hover:bg-surface-2">Open order →</Link>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Compact VERTICAL status timeline — Packed → Dispatched → Delivered; reached stages are brand dots joined by a
// brand line, unreached are hollow. A cancelled shipment collapses to Packed → Cancelled (red).
function VerticalTimeline({ r }: { r: any }) {
  const cancelled = r.status === 'CANCELLED';
  const steps: { label: string; at: any; by?: string | null; danger?: boolean }[] = cancelled
    ? [{ label: 'Packed', at: r.packedAt, by: r.packedByName }, { label: 'Cancelled', at: null, danger: true }]
    : [
        { label: 'Packed', at: r.packedAt, by: r.packedByName },
        { label: 'Dispatched', at: r.dispatchedAt },
        { label: 'Delivered', at: r.deliveredAt },
      ];
  return (
    <ol className="relative">
      {steps.map((st, i) => {
        const done = !!st.at || !!st.danger;
        const last = i === steps.length - 1;
        const nextReached = !last && (!!steps[i + 1].at || !!steps[i + 1].danger);
        return (
          <li key={st.label} className="relative flex gap-2.5 pb-3.5 last:pb-0">
            {!last && <span aria-hidden className={`absolute left-1.25 top-4 h-full w-0.5 ${nextReached && !steps[i + 1].danger ? 'bg-brand-500' : 'bg-line'}`} />}
            <span className={`relative z-10 mt-1 size-3 shrink-0 rounded-full ${st.danger ? 'bg-danger-500' : done ? 'bg-brand-500' : 'border border-line bg-surface-3'}`} />
            <div className="min-w-0 text-sm leading-tight">
              <div className={`font-semibold ${st.danger ? 'text-danger-600' : done ? 'text-content' : 'text-subtle'}`}>{st.label}</div>
              <div className="mt-0.5 text-2xs text-muted">{st.at ? dt(st.at) : (st.danger ? (r.notes ?? '—') : 'Pending')}</div>
              {done && st.by && <div className="text-2xs text-subtle">{st.by}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ColHead({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-subtle">{children}</div>;
}
// Key-value grid styled to match the app's antd Descriptions look — brand-green label cells + plain value cells,
// hairline-bordered (the `gap-px bg-line` trick draws the grid lines). Two label/value pairs per row. Uses the
// same brand-50 tint as Descriptions so detail blocks read consistently green (not the cool surface-2 grey).
function DetailGrid({ rows }: { rows: { label: string; value: any }[][] }) {
  const labelCls = 'flex items-center bg-brand-50 px-3 py-1.5 text-xs font-medium text-muted dark:bg-brand-900/25';
  const valueCls = 'flex items-center bg-surface px-3 py-1.5 text-sm text-content';
  const fmt = (v: any) => (v == null || v === '' ? <span className="text-subtle">—</span> : v);
  return (
    <div className="overflow-hidden rounded-control border border-line">
      <div className="grid gap-px bg-line sm:grid-cols-[minmax(84px,auto)_minmax(0,1fr)_minmax(84px,auto)_minmax(0,1fr)]">
        {rows.map((pair, i) => (
          <Fragment key={i}>
            <div className={labelCls}>{pair[0].label}</div>
            <div className={valueCls}>{fmt(pair[0].value)}</div>
            {pair[1] ? (
              <>
                <div className={labelCls}>{pair[1].label}</div>
                <div className={valueCls}>{fmt(pair[1].value)}</div>
              </>
            ) : (
              <>
                <div className="hidden bg-brand-50 dark:bg-brand-900/25 sm:block" />
                <div className="hidden bg-surface sm:block" />
              </>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
function DocChip({ icon, label, value, to, warn }: { icon: React.ReactNode; label: string; value: string; to?: string; warn?: string }) {
  const inner = (
    <span className="inline-flex items-center gap-1.5 rounded-control border border-line bg-surface px-3 py-1.5 text-sm">
      <span className="text-muted">{icon}</span>
      <span className="text-subtle">{label}</span>
      <span className="font-medium text-content">{value}</span>
      {warn ? <span className="text-warning-600">· {warn}</span> : null}
    </span>
  );
  return to ? <Link to={to} onClick={(e) => e.stopPropagation()} className="transition-opacity hover:opacity-80">{inner}</Link> : inner;
}
function Kpi({ title, value, accent, valueCls, icon }: { title: string; value: number; accent: string; valueCls?: string; icon?: React.ReactNode }) {
  return (
    <div className={`rounded-card border border-line border-l-[3px] ${accent} bg-surface px-4 py-3`}>
      <div className="flex items-center gap-1.5 text-xs text-muted">{icon}{title}</div>
      <div className={`mt-0.5 text-2xl font-bold tabular-nums ${valueCls ?? 'text-content'}`}>{value}</div>
    </div>
  );
}
